// Custom cursor — self-drawn 24×24 pixel-art cursor shared via member state
// (custom-cursor-2026-09-19.md §2.2).
//
// Used by both `member.cursor-change` / `member.cursor-changed` (confirmed
// state) and `member.cursor-draft` / `member.cursor-drafted` (live editing
// state) in `events/member.ts`, plus the `room.welcome` / `room.member-joined`
// / `room.resume-ok` member entries (`shared/member.ts`, `events/room.ts`) —
// hence a shared/ payload piece rather than something owned by a single
// domain event file (same rationale as `shared/layout.ts`).
//
// **Why raw RGBA instead of PNG** (custom-cursor-2026-09-19.md §2.2):
// 1. ~3 KB per cursor is cheap enough to inline into every member entry
//    (20 people ≈ 60 KB).
// 2. Live draft broadcasts (up to 5 Hz per editor) stay cheap without a
//    diff-sync scheme — every draft just re-sends the full 2304-byte state.
// 3. Rendering is a straight `ImageData` fill, no decode step.
// 4. Validation is trivial: the only invariant is "decoded length is exactly
//    `CURSOR_RGBA_BYTES`", which the base64-length + charset regex below
//    already guarantees at the schema layer (server still re-checks the
//    decoded byte length as defense-in-depth, §2.4).
//
// Server does not interpret the pixel data at all — it is opaque bytes that
// get relayed to other clients.
import { z } from 'zod';
import { CURSOR_RGBA_BASE64_LENGTH } from '../constants.js';
/**
 * A 24×24 RGBA cursor image, base64-encoded (`CURSOR_RGBA_BASE64_LENGTH`
 * chars, no padding — see `CURSOR_RGBA_BYTES`'s doc for why 2304 bytes
 * base64-encodes to an exact, pad-free length). The regex restricts the
 * alphabet to standard base64 (`A-Za-z0-9+/`) with no `=` padding, which
 * combined with the fixed length guarantees `atob`/`Buffer.from(..., 'base64')`
 * decodes to exactly `CURSOR_RGBA_BYTES` bytes.
 */
export const customCursorSchema = z.object({
    rgba: z
        .string()
        .length(CURSOR_RGBA_BASE64_LENGTH)
        .regex(/^[A-Za-z0-9+/]+$/, 'must be standard base64 (no padding)'),
});
//# sourceMappingURL=cursor.js.map